import React, { useState, useEffect, useRef } from "react"
import withStyles from "react-jss"
import { gsap } from "gsap"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
import fonts from "./assets/fonts"
import colors from "./data/colors"
import globalStyles from "./data/globalStyles"
import Header from "./layouts/Header"
import Quiz from "./layouts/Quiz"
import Result from "./layouts/Result"
import ErrorBoundary from "./ErrorBoundary"
import { getScrollPosition } from "./utils/helper"
import useDebouncedResizeObserver from "./hooks/useDebouncedResizeObserver"
import { BREAKPOINT, ANIMATION_DURATION } from "./data/uniqueValues"
import smoothscroll from "smoothscroll-polyfill"
import useWindowSize from "./hooks/useWindowSize"
// import { rawData } from "./data/rawData"
// import { formatFinalData } from "./utils/helper"

// Safari doesn't support window.scrollTo smooth scrolling.
// Polyfill for that
smoothscroll.polyfill()

const styles = {
  ...fonts,
  ...globalStyles,
  app: {
    position: "relative",
    scrollBehaviour: "smooth",
  },
  svg: {
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    zIndex: 100,
  },
}

const App = ({ classes }) => {
  const {
    ref: appRef,
    width: appWidth,
    height: appHeight,
  } = useDebouncedResizeObserver(500)

  const [showResults, setShowResults] = useState(false)
  const [headphoneId, setHeadphoneId] = useState(null)

  const [resetQuiz, setResetQuiz] = useState(false)

  const [paths, setPaths] = useState([])

  const [startButtonState, setStartButtonState] = useState(false)

  const startButtonOnClick = () => {
    if (startButtonState) return
    setStartButtonState(true)
    scrollToQuiz()
  }

  useEffect(() => {
    gsap.registerPlugin(MotionPathPlugin)
  }, [])

  useEffect(() => {
    if (paths.length > 1) {
      // Animate the first line
      const lastBeforePath = paths[paths.length - 2]
      const lastBeforePathRef = lastBeforePath.ref.current
      gsap.to([lastBeforePathRef], {
        strokeDashoffset: lastBeforePath.strokeDashoffset / 2,
        duration: ANIMATION_DURATION,
      })

      // Animate the second line
      const lastPath = paths[paths.length - 1]
      const lastPathRef = lastPath.ref.current
      gsap.to([lastPathRef], {
        strokeDashoffset: lastPath.strokeDashoffset / 2,
        duration: ANIMATION_DURATION,
        delay: ANIMATION_DURATION,
      })
    }
  }, [paths])

  const svgRef = useRef(null)
  const quizRef = useRef(null)
  const resultRef = useRef(null)

  const headerCardRef = useRef(null)
  const startRef = useRef(null)
  const headphoneRef = useRef(null)

  const [startQuiz, setStartQuiz] = useState(false)

  const scrollToQuiz = () => {
    setStartQuiz(true)
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        top: getScrollPosition(quizRef, "top", "toFirstQ"),
      })
    }, 100)
    if (window.parent && window.parent.dataLayer) {
      window.parent.dataLayer.push({
        event: "datavisualization-wiredHeadphoneQuiz-startQuizClick",
      })
    }
  }

  const scrollToHeader = () => {
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        top: getScrollPosition(headerCardRef, "top"),
      })
    }, 100)
    setStartButtonState(false)
    setStartQuiz(false)
  }

  useEffect(() => {
    if (resetQuiz) {
      if (window.parent && window.parent.dataLayer) {
        window.parent.dataLayer.push({
          event: `datavisualization-wiredHeadphoneQuiz-startOverClick`,
        })
      }
    }
  }, [resetQuiz])

  // useEffect(() => {
  //   const test = formatFinalData(rawData)
  //   console.log(test)
  // }, [])

  const { width } = useWindowSize()
  const strokeWidth = width >= 950 ? 6 : width >= 780 ? 5 : 4

  return (
    <ErrorBoundary>
      <div className={classes.app} ref={appRef}>
        <Header
          headerCardRef={headerCardRef}
          startRef={startRef}
          headphoneRef={headphoneRef}
          startButtonState={startButtonState}
          startButtonOnClick={startButtonOnClick}
        />
        <Quiz
          showResults={showResults}
          setShowResults={setShowResults}
          setHeadphoneId={setHeadphoneId}
          quizRef={quizRef}
          resultRef={resultRef}
          resetQuiz={resetQuiz}
          setResetQuiz={setResetQuiz}
          scrollToHeader={scrollToHeader}
          headerCardRef={headerCardRef}
          startRef={startRef}
          headphoneRef={headphoneRef}
          setPaths={setPaths}
          appWidth={appWidth}
          appHeight={appHeight}
          svgRef={svgRef}
          startQuiz={startQuiz}
        />
        {showResults ? (
          <Result
            id={headphoneId}
            ref={resultRef}
            setResetQuiz={setResetQuiz}
          />
        ) : null}
        <svg
          className={classes.svg}
          width="100%"
          height="100%"
          ref={svgRef}
          style={{
            opacity: window.innerWidth > BREAKPOINT ? 1 : 0,
          }}>
          {paths.map((d, i) => {
            if (i === 0)
              return (
                <path
                  key={i}
                  d={d.d}
                  stroke={colors.black}
                  strokeWidth={strokeWidth}
                  strokeLinejoin="round"
                  fill="none"
                />
              )
            else
              return (
                <g key={i}>
                  <path
                    d={d.d}
                    stroke={colors.black}
                    strokeWidth={strokeWidth}
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    {...d}
                    stroke="white"
                    strokeWidth={strokeWidth + 1}
                    strokeLinejoin="round"
                    fill="none"
                  />
                </g>
              )
          })}
        </svg>
      </div>
    </ErrorBoundary>
  )
}

export default withStyles(styles)(App)
